/**
 * VIVA HUB CRM — operator documentation and visual workflows
 */

import React from 'react'
import { Link } from 'react-router-dom'
import HubLayout from '../components/hub/HubLayout'
import CrmFlowDiagram, { CrmStatusPipeline } from '../components/hub/crm/CrmFlowDiagram'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import {
  CRM_ASSIGNEE_GUIDE,
  CRM_COMING_SOON,
  CRM_ENTRY_CHANNELS,
  CRM_FAQ,
  CRM_HELP_INTRO,
  CRM_HELP_SECTIONS,
  CRM_LEAD_FILE_GUIDE,
  CRM_MANUAL_INTAKE_GUIDE,
  CRM_LIST_GUIDE,
  CRM_QUICK_START,
  CRM_STATUS_DESCRIPTIONS,
  CRM_WORKFLOWS,
  getStatusLabel,
} from '../content/crm-help'
import { statusBadgeClass } from '../lib/crm-display'

function HelpNav() {
  return (
    <nav
      className="sticky top-20 z-10 rounded-lg border border-gray-200 bg-white p-4 shadow-sm mb-8"
      aria-label="Guide sections"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
        On this page
      </p>
      <ul className="flex flex-wrap gap-x-4 gap-y-2">
        {CRM_HELP_SECTIONS.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              className="text-sm text-green-700 hover:underline font-medium"
            >
              {section.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default function HubCRMHelp() {
  return (
    <HubLayout appName="CRM — Help">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Link to="/hub/crm" className="text-sm text-primary hover:underline">
          ← Back to cases
        </Link>
      </div>

      <div className="w-full">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{CRM_HELP_INTRO.title}</h1>
          <p className="mt-3 text-base text-gray-700 leading-relaxed max-w-4xl">
            {CRM_HELP_INTRO.summary}
          </p>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed max-w-4xl">
            {CRM_HELP_INTRO.replaces}
          </p>
        </header>

        <HelpNav />

        <div className="space-y-12 w-full">
        <section id="quick-start" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick start</h2>
          <ol className="space-y-4">
            {CRM_QUICK_START.map((item, index) => (
              <li key={item.title} className="flex gap-4">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-bold text-gray-800">
                  {index + 1}
                </span>
                <div>
                  <h3 className="font-semibold text-gray-900">{item.title}</h3>
                  <p className="mt-1 text-sm text-gray-700 leading-relaxed">{item.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section id="statuses" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Case statuses</h2>
          <p className="text-sm text-gray-600 mb-4 leading-relaxed">
            Move cases forward as you work. The usual order is below; you can also set On hold,
            Lost, or Not interested when needed.
          </p>
          <CrmStatusPipeline />
          <div className="mt-6 rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-800">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-800">What it means</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-800">What to do</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {CRM_STATUS_DESCRIPTIONS.map((row) => (
                  <tr key={row.status} className="bg-white">
                    <td className="px-4 py-3 align-top w-[11rem]">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(row.status)}`}
                      >
                        {getStatusLabel(row.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700">{row.meaning}</td>
                    <td className="px-4 py-3 align-top text-gray-700">{row.youDo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="list" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{CRM_LIST_GUIDE.title}</h2>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Filters</h3>
          <ul className="space-y-2 mb-6">
            {CRM_LIST_GUIDE.filters.map((f) => (
              <li key={f.name} className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">{f.name}</span> — {f.detail}
              </li>
            ))}
          </ul>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Columns</h3>
          <ul className="space-y-2">
            {CRM_LIST_GUIDE.columns.map((c) => (
              <li key={c.name} className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">{c.name}</span> — {c.detail}
              </li>
            ))}
          </ul>
        </section>

        <section id="lead-file" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{CRM_LEAD_FILE_GUIDE.title}</h2>
          <ul className="space-y-3">
            {CRM_LEAD_FILE_GUIDE.sections.map((s) => (
              <li
                key={s.name}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm"
              >
                <span className="font-semibold text-gray-900">{s.name}</span>
                <span className="text-gray-700"> — {s.detail}</span>
              </li>
            ))}
          </ul>
        </section>

        <section id="manual-intake" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {CRM_MANUAL_INTAKE_GUIDE.title}
          </h2>
          <p className="text-sm text-gray-700 leading-relaxed max-w-4xl mb-6">
            {CRM_MANUAL_INTAKE_GUIDE.intro}
          </p>

          <div className="grid gap-6 lg:grid-cols-2 mb-8">
            <div className="rounded-lg border border-green-200 bg-green-50/50 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">When to use</h3>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-700">
                {CRM_MANUAL_INTAKE_GUIDE.whenToUse.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">When not to use</h3>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-700">
                {CRM_MANUAL_INTAKE_GUIDE.whenNotToUse.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-800 mb-2">Step by step</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 mb-8 max-w-4xl">
            {CRM_MANUAL_INTAKE_GUIDE.steps.map((step) => (
              <li key={step} className="leading-relaxed ps-1">
                {step}
              </li>
            ))}
          </ol>

          <h3 className="text-sm font-semibold text-gray-800 mb-2">Buttons on the case file</h3>
          <ul className="space-y-2 mb-8">
            {CRM_MANUAL_INTAKE_GUIDE.buttons.map((b) => (
              <li
                key={b.name}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm"
              >
                <span className="font-semibold text-gray-900">{b.name}</span>
                <span className="text-gray-700"> — {b.detail}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-sm font-semibold text-gray-800 mb-2">What CRM does after you save</h3>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-700 max-w-4xl">
            {CRM_MANUAL_INTAKE_GUIDE.whatHappens.map((item) => (
              <li key={item} className="leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section id="assignee" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{CRM_ASSIGNEE_GUIDE.title}</h2>
          <p className="text-sm text-gray-700 leading-relaxed">{CRM_ASSIGNEE_GUIDE.body}</p>
        </section>

        <section id="workflows" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Step-by-step workflows</h2>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            Use these diagrams when you are unsure what to do next. They match the team process
            documented for VIVA CRM (same flows as the technical sequence diagrams, in plain
            language).
          </p>
          <div className="space-y-8">
            {CRM_WORKFLOWS.map((flow) => (
              <CrmFlowDiagram
                key={flow.id}
                title={flow.title}
                subtitle={flow.subtitle}
                steps={flow.steps}
                note={flow.note}
              />
            ))}
          </div>
        </section>

        <section id="sources" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Where cases come from</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {CRM_ENTRY_CHANNELS.map((ch) => (
              <li
                key={ch.key}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm"
              >
                <p className="font-semibold text-gray-900">{ch.label}</p>
                <p className="mt-1 text-gray-600">{ch.hint}</p>
              </li>
            ))}
          </ul>
        </section>

        <section id="faq" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">FAQ</h2>
          <Accordion
            type="multiple"
            className="rounded-lg border border-border bg-card px-4"
          >
            {CRM_FAQ.map((item, index) => (
              <AccordionItem key={item.q} value={`crm-faq-${index}`}>
                <AccordionTrigger className="text-sm hover:text-primary hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="leading-relaxed text-foreground">{item.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section id="coming-soon" className="scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Coming in a later update</h2>
          <p className="text-sm text-gray-600 mb-3">
            These features are planned after the current CRM release (Phase 2 and beyond):
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            {CRM_COMING_SOON.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        </div>

        <footer className="mt-12 flex flex-wrap gap-4 border-t border-border pt-6">
          <Button asChild variant="success" size="sm">
            <Link to="/hub/crm">Open CRM cases</Link>
          </Button>
        </footer>
      </div>
    </HubLayout>
  )
}
